import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { PersonAdd } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, List, Stack, Typography } from '@mui/material';

import FloatingBottomButton from '@/components/common/FloatingBottomButton';
import SearchNoResult from '@/shared/ui/no-result-display/SearchNoResult';
import { SearchComponent } from '@/components/common/Search';
import { SortDrawer, type SortValue } from '@/components/common/SortDrawer';

import { useInfiniteUserList } from '../../api/userQueries';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { canManageUsers } from '../../utils/userAccessControlPermissions';
import { createUserAccessListRouteState, getUserAccessListState } from '../../utils/userAccessListState';
import {
  getUserAccessListSearchParams,
  withUserAccessListSearchParams,
  type UserAccessListSearchState,
} from '../../utils/userAccessListSearchParams';
import { userAccessRoutes } from '../../utils/userAccessRoutes';
import { filterDeactivatedUsers, getUserListFeedback, shouldShowWelcomeCard } from '../../utils/userListUtils';
import { UserAccessErrorState } from '../user-access-state-panel/UserAccessErrorState';
import { UserFilterDrawer } from '../user-filter-drawer/UserFilterDrawer';
import { UserListItem } from '../user-list-item/UserListItem';
import { UserListHeader } from './UserListHeader';
import { UserListWelcomeCard } from './UserListWelcomeCard';
import { userListSx } from './UserList.styles';

const PAGE_SIZE = 10;

export function UserList() {
  const userCtx = useCurrentUser();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchText } = getUserAccessListState(location.state);
  const { showDeactivated, sort } = getUserAccessListSearchParams(searchParams);
  const [initialListState] = useState(() => getUserAccessListState(location.state));
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollRestoredRef = useRef(initialListState.scrollTop === 0);
  const lastAppliedScrollTopRef = useRef<number | null>(null);
  const { users, hasMore, isLoading, isFetchingNextPage, isError, loadMoreUsers, retryUsers } = useInfiniteUserList(
    {
      filter: searchText,
      order: sort[0]?.direction ?? 'asc',
    },
    PAGE_SIZE
  );
  const isAllowedToAddUser = canManageUsers(userCtx?.user);
  const visibleUsers = filterDeactivatedUsers(users, showDeactivated);

  useEffect(() => {
    if (scrollRestoredRef.current || (isLoading && users.length === 0)) {
      return;
    }

    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    listElement.scrollTop = initialListState.scrollTop;
    lastAppliedScrollTopRef.current = listElement.scrollTop;
    if (listElement.scrollTop >= initialListState.scrollTop || !hasMore) {
      scrollRestoredRef.current = true;
    }
  }, [initialListState.scrollTop, isLoading, users.length, hasMore]);

  function updateListControls(changes: Partial<UserAccessListSearchState>) {
    scrollRestoredRef.current = true;
    if (listRef.current) listRef.current.scrollTop = 0;
    setSearchParams((current) => withUserAccessListSearchParams(current, changes), {
      replace: true,
      state: createUserAccessListRouteState({ searchText, scrollTop: 0 }),
    });
  }

  function updateSearchText(value: string) {
    scrollRestoredRef.current = true;
    if (listRef.current) listRef.current.scrollTop = 0;
    void navigate(
      { pathname: userAccessRoutes.root, search: location.search },
      {
        replace: true,
        state: createUserAccessListRouteState({ searchText: value, scrollTop: 0 }),
      }
    );
  }

  const { showBackgroundError, showManualLoadMore } = getUserListFeedback({
    hasMore,
    isError,
    isFetchingNextPage,
    loadedUserCount: users.length,
    visibleUserCount: visibleUsers.length,
  });
  const showWelcomeCard = shouldShowWelcomeCard({
    isLoading,
    hasMore,
    searchText,
    users,
    currentUserId: userCtx?.user.id,
    canAddUser: isAllowedToAddUser,
  });

  function handleLoadMore() {
    if (isFetchingNextPage || isError) {
      return;
    }
    void loadMoreUsers();
  }

  function handleRetry() {
    void retryUsers();
  }

  function handleUserSelect(userId: number) {
    const state = createUserAccessListRouteState({ searchText, scrollTop: listRef.current?.scrollTop ?? 0 });
    void navigate({ pathname: userAccessRoutes.root, search: location.search }, { replace: true, state });
    void navigate({ pathname: userAccessRoutes.userDetails(userId), search: location.search }, { state });
  }

  const header = <UserListHeader />;

  if (isError && users.length === 0) {
    return <UserAccessErrorState header={header} onRetry={handleRetry} />;
  }

  return (
    <Stack position="relative" p={1} height="100%" minHeight={0}>
      {header}
      <SearchComponent
        searchText={searchText}
        setSearchText={updateSearchText}
        placeholder={t('userAccessControl.searchPlaceholder')}
        setHistoryMode="userAccessControl"
      />
      {showBackgroundError && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {t('error.dialog.retry')}
            </Button>
          }
        >
          {t('userAccessControl.errors.refreshFailed')}
        </Alert>
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between" minHeight={40}>
        <Typography
          variant="body2"
          color="text.secondary"
          px={1}
          aria-live="polite"
          aria-label={t('userAccessControl.userCount', { count: visibleUsers.length })}
        >
          ({visibleUsers.length})
        </Typography>
        <Stack direction="row">
          <SortDrawer
            options={[{ field: 'name', label: t('userAccessControl.sort.email') }]}
            value={sort}
            onChange={(value: SortValue[]) => updateListControls({ sort: value })}
            title={t('userAccessControl.sort.title')}
          />
          <UserFilterDrawer
            showDeactivated={showDeactivated}
            onShowDeactivatedChange={(value) => updateListControls({ showDeactivated: value })}
          />
        </Stack>
      </Stack>
      <List
        ref={listRef}
        component="div"
        role="list"
        sx={userListSx}
        onScroll={(event) => {
          if (!scrollRestoredRef.current && event.currentTarget.scrollTop !== lastAppliedScrollTopRef.current) {
            scrollRestoredRef.current = true;
          }
        }}
      >
        {isLoading && users.length === 0 && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        <InfiniteScroll
          pageStart={0}
          initialLoad={false}
          loadMore={handleLoadMore}
          hasMore={hasMore}
          useWindow={false}
          loader={
            <Box key="user-list-loader" display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          }
        >
          {visibleUsers.map((user) => (
            <UserListItem key={user.id} user={user} onSelect={() => handleUserSelect(user.id)} />
          ))}
          {showWelcomeCard && <UserListWelcomeCard />}
        </InfiniteScroll>
        {showManualLoadMore && (
          <Box display="flex" justifyContent="center" p={2}>
            <Button variant="outlined" onClick={handleLoadMore}>
              {t('userAccessControl.actions.loadMore')}
            </Button>
          </Box>
        )}
        {!isLoading && !hasMore && visibleUsers.length === 0 && (
          <SearchNoResult searchText={searchText} onClearSearch={() => updateSearchText('')} />
        )}
      </List>
      {isAllowedToAddUser && (
        <FloatingBottomButton
          icon={<PersonAdd />}
          onAction={() => navigate(userAccessRoutes.createUser)}
          position="absolute"
          title={t('userAccessControl.newUser')}
        />
      )}
    </Stack>
  );
}
